import React, { useState, useEffect } from 'react';
import { stockInwardAPI, stockIssueAPI, stockTransferAPI, stockAPI, propertyAPI, storeAPI, itemAPI, categoryAPI, vendorAPI, reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { BarChart3, Download, FileSpreadsheet, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, Package } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatNumber, formatCurrency, downloadCSV } from '../lib/utils';

export const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('stock');
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // Report data
  const [stockReport, setStockReport] = useState([]);
  const [inwardReport, setInwardReport] = useState([]);
  const [issueReport, setIssueReport] = useState([]);
  const [transferReport, setTransferReport] = useState([]);
  const [itemLedger, setItemLedger] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    property_id: 'all',
    store_id: 'all',
    category_id: 'all',
    vendor_id: 'all',
    item_id: 'all',
    date_from: '',
    date_to: ''
  });

  const fetchMasterData = async () => {
    try {
      const [propertiesRes, storesRes, itemsRes, categoriesRes, vendorsRes] = await Promise.all([
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        categoryAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' })
      ]);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchReport = async (reportType) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.property_id !== 'all') params.property_id = filters.property_id;
      if (filters.store_id !== 'all') params.store_id = filters.store_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.vendor_id !== 'all') params.vendor_id = filters.vendor_id;

      switch (reportType) {
        case 'stock':
          const stockRes = await stockAPI.getAll(params);
          setStockReport(stockRes.data);
          break;
        case 'inward':
          const inwardRes = await stockInwardAPI.getAll(params);
          setInwardReport(inwardRes.data);
          break;
        case 'issue':
          const issueRes = await stockIssueAPI.getAll(params);
          setIssueReport(issueRes.data);
          break;
        case 'transfer':
          const transferRes = await stockTransferAPI.getAll(params);
          setTransferReport(transferRes.data);
          break;
        case 'ledger':
          if (filters.item_id !== 'all') {
            const ledgerRes = await reportsAPI.getItemLedger({
              item_id: filters.item_id,
              ...params
            });
            setItemLedger(ledgerRes.data);
          } else {
            toast.error('Please select an item for ledger report');
          }
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(activeTab);
  }, [activeTab]);

  const handleExport = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    downloadCSV(data, filename);
    toast.success('Report exported successfully');
  };

  const exportStockReport = () => {
    const exportData = stockReport.map(s => ({
      'Item Name': s.item_name,
      'Item Code': s.item_code || '-',
      'Category': s.category_name || '-',
      'Store': s.store_name,
      'Property': s.property_name,
      'Quantity': s.quantity,
      'Unit': s.unit_name || '-',
      'Min Stock': s.min_stock_level
    }));
    handleExport(exportData, 'stock_report');
  };

  const exportInwardReport = () => {
    const exportData = inwardReport.flatMap(r => 
      r.items.map(item => ({
        'Transaction #': r.transaction_number,
        'Date': r.date,
        'Property': r.property_name,
        'Store': r.store_name,
        'Vendor': r.vendor_name || '-',
        'Invoice #': r.invoice_number || '-',
        'Item': item.item_name,
        'Quantity': item.quantity,
        'Unit': item.unit_name,
        'Rate': item.rate,
        'Amount': item.amount
      }))
    );
    handleExport(exportData, 'inward_report');
  };

  const exportIssueReport = () => {
    const exportData = issueReport.flatMap(r =>
      r.items.map(item => ({
        'Transaction #': r.transaction_number,
        'Date': r.date,
        'Property': r.property_name,
        'From Store': r.from_store_name,
        'Issue To': r.issue_to,
        'Item': item.item_name,
        'Quantity': item.quantity,
        'Unit': item.unit_name,
        'Issued By': r.issued_by_name
      }))
    );
    handleExport(exportData, 'issue_report');
  };

  const exportTransferReport = () => {
    const exportData = transferReport.flatMap(r =>
      r.items.map(item => ({
        'Transaction #': r.transaction_number,
        'Date': r.date,
        'Property': r.property_name,
        'From Store': r.from_store_name,
        'To Store': r.to_store_name,
        'Item': item.item_name,
        'Quantity': item.quantity,
        'Unit': item.unit_name
      }))
    );
    handleExport(exportData, 'transfer_report');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground mt-1">View and export inventory reports</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select 
                value={filters.property_id} 
                onValueChange={(v) => setFilters({...filters, property_id: v})}
              >
                <SelectTrigger data-testid="report-filter-property">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Store</Label>
              <Select 
                value={filters.store_id} 
                onValueChange={(v) => setFilters({...filters, store_id: v})}
              >
                <SelectTrigger data-testid="report-filter-store">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date From</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                data-testid="report-filter-date-from"
              />
            </div>
            <div className="space-y-2">
              <Label>Date To</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                data-testid="report-filter-date-to"
              />
            </div>
            {activeTab === 'ledger' && (
              <div className="space-y-2">
                <Label>Item *</Label>
                <Select 
                  value={filters.item_id} 
                  onValueChange={(v) => setFilters({...filters, item_id: v})}
                >
                  <SelectTrigger data-testid="report-filter-item">
                    <SelectValue placeholder="Select Item" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Select Item</SelectItem>
                    {items.map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2 flex items-end">
              <Button onClick={() => fetchReport(activeTab)} data-testid="apply-filters-btn">
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="stock" data-testid="tab-stock">
            <Package className="h-4 w-4 mr-2" /> Stock
          </TabsTrigger>
          <TabsTrigger value="inward" data-testid="tab-inward">
            <ArrowDownToLine className="h-4 w-4 mr-2" /> Inward
          </TabsTrigger>
          <TabsTrigger value="issue" data-testid="tab-issue">
            <ArrowUpFromLine className="h-4 w-4 mr-2" /> Issue
          </TabsTrigger>
          <TabsTrigger value="transfer" data-testid="tab-transfer">
            <ArrowLeftRight className="h-4 w-4 mr-2" /> Transfer
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Ledger
          </TabsTrigger>
        </TabsList>

        {/* Stock Report */}
        <TabsContent value="stock">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Current Stock Report</CardTitle>
              <Button variant="outline" onClick={exportStockReport} data-testid="export-stock-report">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner h-8 w-8" /></div>
              ) : stockReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Store</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead>Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stockReport.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{s.item_name}</TableCell>
                          <TableCell>{s.item_code || '-'}</TableCell>
                          <TableCell>{s.category_name || '-'}</TableCell>
                          <TableCell>{s.store_name}</TableCell>
                          <TableCell className="text-right">{formatNumber(s.quantity)}</TableCell>
                          <TableCell>{s.unit_name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inward Report */}
        <TabsContent value="inward">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Stock Inward Report</CardTitle>
              <Button variant="outline" onClick={exportInwardReport} data-testid="export-inward-report">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner h-8 w-8" /></div>
              ) : inwardReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Transaction #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Store</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inwardReport.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono">{r.transaction_number}</TableCell>
                          <TableCell>{formatDate(r.date)}</TableCell>
                          <TableCell>{r.store_name}</TableCell>
                          <TableCell>{r.vendor_name || '-'}</TableCell>
                          <TableCell>{r.invoice_number || '-'}</TableCell>
                          <TableCell className="text-right">{formatCurrency(r.total_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Issue Report */}
        <TabsContent value="issue">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Stock Issue Report</CardTitle>
              <Button variant="outline" onClick={exportIssueReport} data-testid="export-issue-report">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner h-8 w-8" /></div>
              ) : issueReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Transaction #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>From Store</TableHead>
                        <TableHead>Issue To</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Issued By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {issueReport.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono">{r.transaction_number}</TableCell>
                          <TableCell>{formatDate(r.date)}</TableCell>
                          <TableCell>{r.from_store_name}</TableCell>
                          <TableCell>{r.issue_to}</TableCell>
                          <TableCell>{r.items?.length || 0} items</TableCell>
                          <TableCell>{r.issued_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfer Report */}
        <TabsContent value="transfer">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Stock Transfer Report</CardTitle>
              <Button variant="outline" onClick={exportTransferReport} data-testid="export-transfer-report">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner h-8 w-8" /></div>
              ) : transferReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Transaction #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>From Store</TableHead>
                        <TableHead>To Store</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Initiated By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transferReport.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono">{r.transaction_number}</TableCell>
                          <TableCell>{formatDate(r.date)}</TableCell>
                          <TableCell>{r.from_store_name}</TableCell>
                          <TableCell>{r.to_store_name}</TableCell>
                          <TableCell>{r.items?.length || 0} items</TableCell>
                          <TableCell>{r.initiated_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Item Ledger */}
        <TabsContent value="ledger">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Item Ledger Report</CardTitle>
              <Button variant="outline" onClick={() => handleExport(itemLedger, 'item_ledger')} data-testid="export-ledger-report">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner h-8 w-8" /></div>
              ) : filters.item_id === 'all' ? (
                <div className="text-center py-8 text-muted-foreground">Please select an item to view ledger</div>
              ) : itemLedger.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No transactions found for this item</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Transaction #</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">In</TableHead>
                        <TableHead className="text-right">Out</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemLedger.map((txn, i) => (
                        <TableRow key={i}>
                          <TableCell>{formatDate(txn.date)}</TableCell>
                          <TableCell className="font-mono">{txn.transaction_number}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              txn.type === 'Inward' ? 'bg-green-100 text-green-700' :
                              txn.type === 'Issue' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {txn.type}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {txn.quantity_in > 0 ? `+${formatNumber(txn.quantity_in)}` : '-'}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {txn.quantity_out > 0 ? `-${formatNumber(txn.quantity_out)}` : '-'}
                          </TableCell>
                          <TableCell>{txn.reference || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
